import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import React from 'react';
import zeplinColor from '../../../theme/Color';

interface StyledTabsProps {
  children?: React.ReactNode;
  value: number;
  onChange: (event: React.SyntheticEvent, newValue: number) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  dir?: string;
  index: number;
  value: number;
}

interface customTabProps {
  id: number;
  label: string;
  Component: React.ReactNode;
  isHidden: boolean;
}

interface IProps {
  tabs: customTabProps[];
  handleChange: (event: React.SyntheticEvent, newValue: number) => void;
  value: number;
}

const StyledTabs = styled((props: StyledTabsProps) => (
  <Tabs {...props} slotProps={{ indicator: <span className='MuiTabs-indicatorSpan' /> }} />
))({
  marginBottom: '0px',
  '@media (min-width:1700px)': {
    marginBottom: '0px',
  },
  '& .MuiTabs-flexContainer': {
    display: 'flex',
    gap: '8px',
    scrollbarWidth: 'none',
    overflow: 'scroll',
    flexWrap: 'wrap',
  },
  '& .MuiTabs-indicator': {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    width: 0,
  },
  '& .MuiTabs-indicatorSpan': {
    maxWidth: 0,
    width: 0,
    backgroundColor: 'none',
  },
  '& .MuiTabs-root': {
    minHeight: '32px',
  },
  '&.MuiTabs-root': {
    minHeight: '32px',
  },
});

interface StyledTabProps {
  label: string;
}

const StyledTab = styled((props: StyledTabProps) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    textTransform: 'none',
    borderRadius: '50px',
    minHeight: '0px',
    backgroundColor: zeplinColor.Neutral3,
    color: zeplinColor.BasicBlack,
    padding: '12px 19px 12px 19px',
    fontSize: '12px',
    '@media (min-width:1700px)': {
      padding: '15px 24px 15px 24px',
      fontSize: '14px',
    },
    fontWeight: 400,
    '&.MuiChip-root': {},

    '&:not(.Mui-selected):hover': {
      backgroundColor: zeplinColor.Neutral4,
    },
    '&.Mui-selected': {
      color: zeplinColor.Neutral1,
      backgroundColor: zeplinColor.BasicBlack,
    },
  }),
);

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
      style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'auto' }}
    >
      {value === index && (
        <Box
          sx={{
            p: '0px',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'auto',
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `full-width-tab-${index}`,
    'aria-controls': `full-width-tabpanel-${index}`,
  };
}

function CustomTab({ tabs, handleChange, value }: IProps) {
  return (
    <Box
      sx={{
        width: '100%',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: '12px', xl: '16px' },
        position: 'relative',
        minHeight: 0,
      }}
    >
      <StyledTabs value={value} onChange={handleChange} aria-label='styled tabs example'>
        {tabs.map((option) => (
          <StyledTab label={option.label} key={option.id} {...a11yProps(option.id)} />
        ))}
      </StyledTabs>
      {tabs.map(
        ({ Component }, i: number) =>
          !tabs[i].isHidden && (
            <TabPanel value={value} index={i} key={i}>
              {Component}
            </TabPanel>
          ),
      )}
    </Box>
  );
}

export default CustomTab;
